// Read-only student details modal - mirrors ViewStaffModal for the student management page
import React, { useState, useEffect } from 'react';
import { X, Mail, Phone, MapPin, Calendar, User, Building2, Loader2, GraduationCap, Contact, Home, Heart, FileText, ExternalLink, CheckCircle, Users, BookOpen, Accessibility } from 'lucide-react';
import type { StudentUser } from '@/services/userService';
import { adminGetStudentProfile, adminGetStudentDocuments, type UserProfileResponse, type StudentDocument } from '@/services/profileService';
import { StudentFinancialSection } from './StudentFinancialSection';
import { formatPhoneWithCountry } from '@/shared/constants/phoneCodes';
import { countryDisplayName } from '@/shared/constants/countryCode';
import { emergencyRelationLabel } from '@/shared/constants/emergencyRelation';

// Country code to full name mapping (French)
interface ViewStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: StudentUser | null;
}

const statusLabels: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: 'Actif', color: 'bg-green-100 text-green-800' },
  PENDING: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  INACTIVE: { label: 'Inactif', color: 'bg-gray-100 text-gray-800' },
  SUSPENDED: { label: 'Suspendu', color: 'bg-red-100 text-red-800' },
  WITHDRAWN: { label: 'Archive', color: 'bg-orange-100 text-orange-800' },
};

// Profile submission status badges
const profileStatusLabels: Record<string, { label: string; color: string }> = {
  APPROVED: { label: 'Validé', color: 'bg-green-100 text-green-800' },
  PENDING: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  PENDING_VALIDATION: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  DRAFT: { label: 'Brouillon', color: 'bg-gray-100 text-gray-800' },
  REJECTED: { label: 'Rejeté', color: 'bg-red-100 text-red-800' },
};

// Emergency contact relationship labels (slugs stored in DB)
// Handicap code labels (matches DirectEditStudentProfileModal options)
const HANDICAP_LABELS: Record<string, string> = {
  N: 'Aucun',
  A: 'Auditif',
  M: 'Moteur',
  V: 'Visuel',
  AM: 'Auditif et Moteur',
  AV: 'Auditif et Visuel',
  MV: 'Moteur et Visuel',
  T: 'Tous',
  XX: 'Autre',
};

// Document type labels (matches backend StudentDocumentType enum, student types)
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  CIN_FRONT: 'CIN (Recto)',
  CIN_BACK: 'CIN (Verso)',
  PASSPORT: 'Passeport',
  PHOTO: 'Photo d\'identité',
  DIPLOME_BAC_SCAN: 'Diplôme du Bac',
  RELEVES_NOTES_BAC: 'Relevés de notes Bac',
  DIPLOME_ACCES_SCAN: 'Diplôme d\'accès',
  RELEVES_NOTES_ACCES: 'Relevés diplôme accès',
  CV: 'CV',
  LETTRE_MOTIVATION: 'Lettre de motivation',
};

// Physical document flags with French labels (order matters for display)
type PhysicalDocKey =
  | 'physicalDocStudentCin'
  | 'physicalDocStudentBac'
  | 'physicalDocStudentReleveBac'
  | 'physicalDocStudentDernierDiplome'
  | 'physicalDocStudentReleveDiplome'
  | 'physicalDocStudentReleveAnneesPrecedentes'
  | 'physicalDocStudentCv'
  | 'physicalDocStudentLettreMotivation';

const PHYSICAL_DOC_ITEMS: { key: PhysicalDocKey; label: string }[] = [
  { key: 'physicalDocStudentCin', label: 'CIN / Pièce d\'identité' },
  { key: 'physicalDocStudentBac', label: 'Diplôme du Bac' },
  { key: 'physicalDocStudentReleveBac', label: 'Relevé de notes du Bac' },
  { key: 'physicalDocStudentDernierDiplome', label: 'Dernier diplôme' },
  { key: 'physicalDocStudentReleveDiplome', label: 'Relevé dernier diplôme' },
  { key: 'physicalDocStudentReleveAnneesPrecedentes', label: 'Relevés années précédentes' },
  { key: 'physicalDocStudentCv', label: 'CV (optionnel)' },
  { key: 'physicalDocStudentLettreMotivation', label: 'Lettre de motivation (optionnel)' },
];

// Helper to check if diplome is BAC or similar (same logic as StudentPhysicalDocumentsTrackingModal)
const isBacDiplome = (diplome?: string): boolean => {
  if (!diplome) return true; // Default to BAC if not specified
  const normalized = diplome.toLowerCase().trim();
  return normalized === 'bac' ||
         normalized === 'baccalauréat' ||
         normalized === 'baccalaureat' ||
         normalized.startsWith('bac ') ||
         normalized.includes('baccalaur');
};

// Build an absolute URL for a stored document (fileUrl is a /uploads/... key)
const getDocumentUrl = (fileUrl: string): string => {
  if (fileUrl.startsWith('/')) {
    return `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${fileUrl}`;
  }
  return fileUrl;
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

// Format phone for display with fallback to dash
const formatPhoneDisplay = (phone?: string | null, countryCode?: string | null): string => {
  const formatted = formatPhoneWithCountry(phone ?? undefined, countryCode ?? undefined);
  return formatted || '-';
};

// Info Item component for consistent display
const InfoItem: React.FC<{
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  iconColor?: string;
}> = ({ icon: Icon, label, value, iconColor = 'text-gray-400' }) => (
  <div className="flex items-start gap-3">
    <Icon size={18} className={`${iconColor} mt-0.5`} />
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm text-gray-900">{value || '-'}</p>
    </div>
  </div>
);

export const ViewStudentModal: React.FC<ViewStudentModalProps> = ({
  isOpen,
  onClose,
  student,
}) => {
  // Full profile data
  const [fullProfile, setFullProfile] = useState<UserProfileResponse | null>(null);
  const [documents, setDocuments] = useState<StudentDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load full profile and documents when modal opens
  useEffect(() => {
    if (isOpen && student) {
      loadData();
    }
    // Reset state when modal closes
    if (!isOpen) {
      setFullProfile(null);
      setDocuments([]);
      setIsLoading(false);
    }
  }, [isOpen, student?.id]);

  const loadData = async () => {
    if (!student) return;
    setIsLoading(true);
    try {
      const [profileData, docsData] = await Promise.all([
        adminGetStudentProfile(student.id),
        adminGetStudentDocuments(student.id),
      ]);
      setFullProfile(profileData);
      setDocuments(docsData || []);
    } catch (error) {
      console.error('Error loading student details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !student) return null;

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  // Extract profile data
  const profile = fullProfile?.profile;
  const studentData = fullProfile?.student;

  // Get full country name from code
  const getCountryName = (code?: string): string => countryDisplayName(code);

  // Physical documents: conditional visibility (same logic as StudentPhysicalDocumentsTrackingModal)
  const hasNonBacDiplome = !isBacDiplome(studentData?.diplomeAcces);
  const isTransferStudent = (studentData?.niveauFormationInitial ?? 1) > 1;
  const visiblePhysicalDocs = PHYSICAL_DOC_ITEMS.filter((item) => {
    if (!hasNonBacDiplome && (item.key === 'physicalDocStudentDernierDiplome' || item.key === 'physicalDocStudentReleveDiplome')) {
      return false;
    }
    if (!isTransferStudent && item.key === 'physicalDocStudentReleveAnneesPrecedentes') {
      return false;
    }
    return true;
  });

  const isPhysicalDocReceived = (key: PhysicalDocKey): boolean => {
    return profile?.[key] === true;
  };

  const profileStatus = student.profileSubmissionStatus
    ? profileStatusLabels[student.profileSubmissionStatus]
    : undefined;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-3xl bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-xl">
            <h2 className="text-xl font-semibold text-gray-900">
              Details de l'etudiant
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-6">
            {/* Profile Header */}
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-700 font-semibold text-xl">
                  {student.firstName[0]}{student.lastName[0]}
                </span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {student.firstName} {student.lastName}
                </h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {student.username && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 font-mono">
                      {student.username}
                    </span>
                  )}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusLabels[student.status]?.color || 'bg-gray-100 text-gray-800'}`}>
                    {statusLabels[student.status]?.label || student.status}
                  </span>
                  {profileStatus && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${profileStatus.color}`}>
                      {profileStatus.label}
                    </span>
                  )}
                  {student.academicYear && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <Calendar size={12} />
                      {student.academicYear.name}
                      {student.academicYear.isCurrent && (
                        <span className="ml-1 px-1 py-0.5 bg-blue-200 rounded text-[10px]">En cours</span>
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-primary-600" size={24} />
                <span className="ml-2 text-sm text-gray-500">Chargement des details...</span>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Section 1: Identite */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Contact size={16} className="text-gray-400" />
                    Identite
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem icon={User} label="Nom" value={student.lastName} />
                    <InfoItem icon={User} label="Prenom" value={student.firstName} />
                    <InfoItem
                      icon={User}
                      label="Nom (Arabe)"
                      value={profile?.nomArabe ? <span dir="rtl">{profile.nomArabe}</span> : '-'}
                    />
                    <InfoItem
                      icon={User}
                      label="Prenom (Arabe)"
                      value={profile?.prenomArabe ? <span dir="rtl">{profile.prenomArabe}</span> : '-'}
                    />
                    <InfoItem icon={Contact} label="CNIE" value={profile?.cnie} />
                    <InfoItem icon={Contact} label="Passeport" value={profile?.passport} />
                    <InfoItem
                      icon={User}
                      label="Sexe"
                      value={profile?.sexe === 'M' ? 'Masculin' : profile?.sexe === 'F' ? 'Feminin' : profile?.sexe || '-'}
                    />
                    <InfoItem icon={Calendar} label="Date de naissance" value={formatDate(profile?.dateNaissance)} />
                    <InfoItem icon={MapPin} label="Pays d'origine" value={getCountryName(profile?.paysOrigine)} />
                    <InfoItem
                      icon={Accessibility}
                      label="Situation de handicap"
                      value={profile?.handicap ? HANDICAP_LABELS[profile.handicap] || profile.handicap : '-'}
                    />
                  </div>
                </div>

                {/* Section 2: Contact */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-200">
                    <User size={16} className="text-gray-400" />
                    Contact
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem icon={Mail} label="Email" value={student.email} />
                    <InfoItem icon={Phone} label="Telephone" value={formatPhoneDisplay(student.phone, student.phoneCountry)} />
                    <InfoItem icon={Home} label="Adresse" value={profile?.adresse} />
                  </div>
                </div>

                {/* Section 3: Scolarite */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-200">
                    <GraduationCap size={16} className="text-gray-400" />
                    Scolarite
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem icon={BookOpen} label="Filiere" value={student.filiereInscrit} />
                    <InfoItem icon={GraduationCap} label="Diplome" value={student.diplomeInscrit} />
                    <InfoItem
                      icon={GraduationCap}
                      label="Niveau"
                      value={student.niveauFormation ? `${student.niveauFormation}${student.niveauFormation === 1 ? 'ère' : 'ème'} année` : '-'}
                    />
                    <InfoItem icon={Building2} label="Campus" value={student.campus?.name} />
                    <InfoItem
                      icon={Users}
                      label="Groupes"
                      value={student.groupes && student.groupes.length > 0 ? student.groupes.map(g => g.name).join(', ') : '-'}
                    />
                    <InfoItem icon={Calendar} label="Annee academique" value={student.academicYear?.name} />
                    <div className="flex items-start gap-3">
                      <CheckCircle size={18} className="text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500">Statut</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${statusLabels[student.status]?.color || 'bg-gray-100 text-gray-800'}`}>
                          {statusLabels[student.status]?.label || student.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <FileText size={18} className="text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500">Statut du profil</p>
                        {profileStatus ? (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${profileStatus.color}`}>
                            {profileStatus.label}
                          </span>
                        ) : (
                          <p className="text-sm text-gray-900">-</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section: Frais Financiers */}
                <StudentFinancialSection studentId={student.studentId} studentCode={student.studentCode} />

                {/* Section 4: Contact d'Urgence */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Heart size={16} className="text-gray-400" />
                    Contact d'Urgence
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem icon={User} label="Nom" value={profile?.urgenceNom} />
                    <InfoItem
                      icon={Heart}
                      label="Relation"
                      value={emergencyRelationLabel(profile?.urgenceRelation) || '-'}
                    />
                    <InfoItem
                      icon={Phone}
                      label="Telephone"
                      value={formatPhoneDisplay(profile?.urgenceNumero, profile?.urgencePhoneCountry)}
                    />
                    <InfoItem icon={Home} label="Adresse" value={profile?.urgenceAdresse} />
                  </div>
                </div>

                {/* Section 5: Parents/Tuteurs lies */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Users size={16} className="text-gray-400" />
                    Parents/Tuteurs lies
                    <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      {student.linkedParents?.length || 0}
                    </span>
                  </h4>
                  {student.linkedParents && student.linkedParents.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {student.linkedParents.map((parent) => (
                        <div
                          key={parent.id}
                          className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg"
                        >
                          <User size={18} className="text-primary-500 mt-0.5 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {parent.firstName} {parent.lastName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {emergencyRelationLabel(parent.relationship) || parent.relationship}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatPhoneDisplay(parent.phone, parent.phoneCountry)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Aucun parent lié</p>
                  )}
                </div>

                {/* Section 6: Documents televerses */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-200">
                    <FileText size={16} className="text-gray-400" />
                    Documents televerses
                    <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      {documents.length}
                    </span>
                  </h4>
                  {documents.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {documents.map((doc) => (
                        <a
                          key={doc.id}
                          href={getDocumentUrl(doc.fileUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50/40 transition-colors group"
                        >
                          <FileText size={18} className="text-primary-500 mt-0.5 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {doc.fileName}
                              {doc.fileSize ? ` · ${formatFileSize(doc.fileSize)}` : ''}
                            </p>
                            {doc.isVerified && (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600 mt-1">
                                <CheckCircle size={12} />
                                Vérifié
                              </span>
                            )}
                          </div>
                          <ExternalLink size={14} className="text-gray-300 group-hover:text-primary-500 mt-1 flex-shrink-0" />
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Aucun document téléversé.</p>
                  )}
                </div>

                {/* Section 7: Documents physiques (originaux recus) */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-200">
                    <CheckCircle size={16} className="text-gray-400" />
                    Documents physiques (originaux recus)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {visiblePhysicalDocs.map((item) => {
                      const received = isPhysicalDocReceived(item.key);
                      return (
                        <div
                          key={item.key}
                          className={`flex items-center gap-3 p-3 border rounded-lg ${received ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                        >
                          {received ? (
                            <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
                          ) : (
                            <X size={18} className="text-gray-400 flex-shrink-0" />
                          )}
                          <span className={`text-sm ${received ? 'text-green-900 font-medium' : 'text-gray-700'}`}>
                            {item.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 flex justify-end px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewStudentModal;
